import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { WIDTH_MODAL_COMPONENTS } from '../../../../constants/addressBook';
import { IColumnMapping } from '../../../../types/addressBook';
import NotificationAlert, { AlertDescription } from '../../../_mui/_alerts_/NotificationAlert';
import { TableMappingRow } from '../components';
import { useUploadModal } from '../UploadModalContext';

const MapColumnsStep: React.FC = () => {
  const theme = useTheme();
  const { mappedColumns, handleMapColumns, userHeaders, profile } = useUploadModal();
  const mapColumnsAlert = profile.copy.mapColumnsAlert;

  const hasRowError = useCallback(
    (row: IColumnMapping): boolean => {
      if (!row.mappedTo) {
        return !row.isOptional;
      }

      // Row has duplicate mapping
      return mappedColumns.some((col) => col.id !== row.id && col.mappedTo === row.mappedTo);
    },
    [mappedColumns],
  );

  const tableRows = useMemo(() => {
    return mappedColumns.map((row, index) => (
      <TableMappingRow
        key={row.id}
        row={row}
        handleMapColumns={handleMapColumns}
        userHeaders={userHeaders}
        isError={hasRowError(row)}
        dataAction="map_columns_step_mapping_row"
        dataIndex={index}
      />
    ));
  }, [mappedColumns, handleMapColumns, userHeaders, hasRowError]);

  const tableStyles = useMemo(
    () => ({
      tableLayout: 'fixed',
      '& .MuiTableCell-root': {
        padding: 0,
      },
      '& .MuiTableHead-root .MuiTableRow-root': {
        height: 24,
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        height: 32,
        lineHeight: '24px',
        fontWeight: theme.typography.fontWeightMedium,
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        verticalAlign: 'middle',
      },
    }),
    [],
  );

  return (
    <>
      <Box sx={{ mt: 3, mb: 3, mx: 'auto', maxWidth: WIDTH_MODAL_COMPONENTS }}>
        <NotificationAlert type={mapColumnsAlert.type}>
          <AlertDescription>{mapColumnsAlert.description}</AlertDescription>
        </NotificationAlert>
      </Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ maxWidth: 800, mx: 'auto', border: 'none', overflowY: 'auto' }}
      >
        <Table sx={tableStyles} aria-label="Column mapping table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Expected column</TableCell>
              <TableCell align="left">Columns in your CSV</TableCell>
              <TableCell align="left">Sample data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableRows}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MapColumnsStep;
